import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import './DosageConverter.css';

const DosageConverter = () => {
  const [activeConverter, setActiveConverter] = useState('weight');
  const [mgValue, setMgValue] = useState('');
  const [mcgValue, setMcgValue] = useState('');
  const [iuValue, setIuValue] = useState('');
  const [iuPeptide, setIuPeptide] = useState('hgh');
  const [mlValue, setMlValue] = useState('');
  const [unitsValue, setUnitsValue] = useState('');
  const [syringeSize, setSyringeSize] = useState('100');

  // Handle weight conversions
  const handleMgChange = (value) => {
    setMgValue(value);
    if (value && !isNaN(value)) {
      setMcgValue((parseFloat(value) * 1000).toString());
    } else {
      setMcgValue('');
    }
  };

  const handleMcgChange = (value) => {
    setMcgValue(value);
    if (value && !isNaN(value)) {
      setMgValue((parseFloat(value) / 1000).toString());
    } else {
      setMgValue('');
    }
  };

  // Handle IU conversions
  const handleIuChange = (value) => {
    setIuValue(value);
    // Conversion logic based on selected peptide
    if (value && !isNaN(value) && iuPeptide === 'hgh') {
      const mgEquivalent = parseFloat(value) / 3;
      setMgValue(mgEquivalent.toFixed(3));
      setMcgValue((mgEquivalent * 1000).toFixed(0));
    }
  };

  // Handle syringe unit conversions
  const handleMlToUnits = (value) => {
    setMlValue(value);
    if (value && !isNaN(value)) {
      const units = parseFloat(value) * parseFloat(syringeSize);
      setUnitsValue(units.toFixed(1));
    } else {
      setUnitsValue('');
    }
  };

  const handleUnitsToMl = (value) => {
    setUnitsValue(value);
    if (value && !isNaN(value)) {
      const ml = parseFloat(value) / parseFloat(syringeSize);
      setMlValue(ml.toFixed(3));
    } else {
      setMlValue('');
    }
  };

  // Common conversions reference data
  const commonConversions = [
    { from: '10mg peptide', to: '10,000mcg', description: 'Standard vial size' },
    { from: '5mg peptide', to: '5,000mcg', description: 'Common vial size' },
    { from: '2mg peptide', to: '2,000mcg', description: 'Small vial size' },
    { from: '1mg', to: '1,000mcg', description: 'Basic conversion' },
    { from: '500mcg', to: '0.5mg', description: 'Half milligram' },
    { from: '250mcg', to: '0.25mg', description: 'Quarter milligram' },
    { from: '100mcg', to: '0.1mg', description: 'Common GH dose' },
    { from: '1mL', to: '100 units', description: 'U-100 syringe' },
    { from: '0.5mL', to: '50 units', description: 'Half syringe' },
    { from: '0.3mL', to: '30 units', description: '30-unit syringe' },
  ];

  return (
    <div className="dosage-converter-page">
      <SEOHead 
        title="Peptide Dosage Converter - mg to mcg to IU Calculator | PeptideDose"
        description="Convert between mg, mcg, and IU for peptides. Calculate syringe units from mL. Free conversion tool for all peptide dosages."
      />

      {/* Hero Section */}
      <div className="dosage-converter-hero">
        <div className="dosage-converter-hero-content">
          <Link to="/" className="dosage-converter-back-link">← Back to Calculator</Link>
          <h1 className="dosage-converter-title">
            Dosage Conversion Tool
            <span className="dosage-converter-subtitle">Convert mg ↔ mcg ↔ IU ↔ Units</span>
          </h1>
          <p className="dosage-converter-description">
            Universal converter for peptide weights, IU values, and syringe units
          </p>
        </div>
      </div>

      {/* Converter Tabs */}
      <div className="dosage-converter-container">
        <div className="dosage-converter-tabs">
          <button 
            className={`dosage-converter-tab ${activeConverter === 'weight' ? 'active' : ''}`}
            onClick={() => setActiveConverter('weight')}
          >
            <span className="dosage-converter-tab-icon">⚖️</span>
            mg ↔ mcg
          </button>
          <button 
            className={`dosage-converter-tab ${activeConverter === 'iu' ? 'active' : ''}`}
            onClick={() => setActiveConverter('iu')}
          >
            <span className="dosage-converter-tab-icon">💊</span>
            IU Converter
          </button>
          <button 
            className={`dosage-converter-tab ${activeConverter === 'syringe' ? 'active' : ''}`}
            onClick={() => setActiveConverter('syringe')}
          >
            <span className="dosage-converter-tab-icon">💉</span>
            Syringe Units
          </button>
        </div>

        {/* Converter Content */}
        <div className="dosage-converter-content">
          {/* Weight Converter (mg ↔ mcg) */}
          {activeConverter === 'weight' && (
            <div className="dosage-converter-section">
              <h2>Weight Converter</h2>
              <div className="dosage-converter-inputs">
                <div className="dosage-converter-input-group">
                  <label>Milligrams (mg)</label>
                  <input
                    type="number"
                    value={mgValue}
                    onChange={(e) => handleMgChange(e.target.value)}
                    placeholder="Enter mg"
                    step="0.001"
                  />
                </div>
                
                <div className="dosage-converter-equals-symbol">⇄</div>
                
                <div className="dosage-converter-input-group">
                  <label>Micrograms (mcg)</label>
                  <input
                    type="number"
                    value={mcgValue}
                    onChange={(e) => handleMcgChange(e.target.value)}
                    placeholder="Enter mcg"
                    step="1"
                  />
                </div>
              </div>
              
              <div className="dosage-converter-formula-box">
                <strong>Formula:</strong> 1 mg = 1,000 mcg
              </div>
            </div>
          )}

          {/* IU Converter */}
          {activeConverter === 'iu' && (
            <div className="dosage-converter-section">
              <h2>International Units (IU) Converter</h2>
              
              <div className="dosage-converter-peptide-selector">
                <label>Select Peptide Type:</label>
                <select value={iuPeptide} onChange={(e) => setIuPeptide(e.target.value)}>
                  <option value="hgh">HGH (Human Growth Hormone)</option>
                  <option value="hcg">HCG (Human Chorionic Gonadotropin)</option>
                  <option value="insulin">Insulin</option>
                  <option value="generic">Generic/Other</option>
                </select>
              </div>
              
              <div className="dosage-converter-inputs">
                <div className="dosage-converter-input-group">
                  <label>IU (International Units)</label>
                  <input
                    type="number"
                    value={iuValue}
                    onChange={(e) => handleIuChange(e.target.value)}
                    placeholder="Enter IU"
                    step="0.1"
                  />
                </div>
                
                <div className="dosage-converter-equals-symbol">→</div>
                
                <div className="dosage-converter-input-group">
                  <label>Weight (mg)</label>
                  <input
                    type="text"
                    value={iuPeptide === 'hgh' && iuValue ? (parseFloat(iuValue) / 3).toFixed(3) : ''}
                    readOnly
                    placeholder="Calculated mg"
                  />
                </div>
              </div>
              
              <div className="dosage-converter-info-box">
                <p><strong>Common IU Conversions:</strong></p>
                <ul>
                  <li>HGH: 1mg = 3 IU</li>
                  <li>HCG: 1 IU = 0.27mcg</li>
                  <li>Insulin: 100 IU = 3.47mg</li>
                </ul>
                <p className="dosage-converter-note">⚠️ IU conversions vary by substance. Always verify with your specific product.</p>
              </div>
            </div>
          )}

          {/* Syringe Unit Converter */}
          {activeConverter === 'syringe' && (
            <div className="dosage-converter-section">
              <h2>Syringe Unit Converter</h2>
              
              <div className="dosage-converter-syringe-selector">
                <label>Select Syringe Type:</label>
                <select value={syringeSize} onChange={(e) => setSyringeSize(e.target.value)}>
                  <option value="100">U-100 (1mL = 100 units)</option>
                  <option value="50">U-50 (0.5mL = 50 units)</option>
                  <option value="30">U-30 (0.3mL = 30 units)</option>
                  <option value="40">U-40 (1mL = 40 units)</option>
                </select>
              </div>
              
              <div className="dosage-converter-inputs">
                <div className="dosage-converter-input-group">
                  <label>Volume (mL)</label>
                  <input
                    type="number"
                    value={mlValue}
                    onChange={(e) => handleMlToUnits(e.target.value)}
                    placeholder="Enter mL"
                    step="0.01"
                  />
                </div>
                
                <div className="dosage-converter-equals-symbol">⇄</div>
                
                <div className="dosage-converter-input-group">
                  <label>Syringe Units</label>
                  <input
                    type="number"
                    value={unitsValue}
                    onChange={(e) => handleUnitsToMl(e.target.value)}
                    placeholder="Enter units"
                    step="1"
                  />
                </div>
              </div>
              
              <div className="dosage-converter-visual-syringe">
                <div className="dosage-converter-syringe-display">
                  <div className="dosage-converter-syringe-barrel">
                    <div 
                      className="dosage-converter-syringe-fill" 
                      style={{height: `${Math.min((parseFloat(unitsValue) || 0) / parseFloat(syringeSize) * 100, 100)}%`}}
                    ></div>
                    <div className="dosage-converter-syringe-markings">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="dosage-converter-marking">
                          <span>{Math.round(syringeSize * (1 - i/4))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="dosage-converter-syringe-label">
                  {unitsValue ? `${unitsValue} units = ${mlValue} mL` : 'Enter a value to see visualization'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Reference Table */}
      <div className="dosage-converter-reference-section">
        <h2>Quick Reference Table</h2>
        <div className="dosage-converter-reference-table">
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {commonConversions.map((conv, index) => (
                <tr key={index}>
                  <td>{conv.from}</td>
                  <td>{conv.to}</td>
                  <td>{conv.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Information Cards */}
      <div className="dosage-converter-info-section">
        <h2>Understanding Dosage Units</h2>
        <div className="dosage-converter-info-grid">
          <div className="dosage-converter-info-card">
            <h3>📏 Weight Units</h3>
            <p><strong>mg (milligram):</strong> 1/1000 of a gram</p>
            <p><strong>mcg (microgram):</strong> 1/1000 of a milligram</p>
            <p><strong>Conversion:</strong> 1mg = 1,000mcg</p>
          </div>
          
          <div className="dosage-converter-info-card">
            <h3>💊 International Units (IU)</h3>
            <p>IU is a measurement of biological activity, not weight. The conversion varies by substance.</p>
            <p>Always check your specific peptide's conversion factor.</p>
          </div>
          
          <div className="dosage-converter-info-card">
            <h3>💉 Syringe Units</h3>
            <p><strong>U-100:</strong> Most common insulin syringe</p>
            <p>100 units = 1mL, so 1 unit = 0.01mL</p>
            <p>The "tick marks" on the syringe represent units.</p>
          </div>
          
          <div className="dosage-converter-info-card">
            <h3>⚠️ Important Tips</h3>
            <ul>
              <li>Always double-check conversions</li>
              <li>Use the correct syringe type</li>
              <li>Verify peptide concentration</li>
              <li>When in doubt, consult a professional</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="dosage-converter-cta">
        <h2>Ready to Calculate Your Dose?</h2>
        <p>Now that you've converted your units, calculate the exact dose for your peptide</p>
        <Link to="/" className="dosage-converter-cta-button">Open Peptide Calculator</Link>
      </div>

      {/* Footer */}
      <footer className="dosage-converter-footer">
        <div className="dosage-converter-footer-links">
          <Link to="/">Main Calculator</Link>
          <Link to="/peptides/compare">Compare Peptides</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/guides">Guides</Link>
        </div>
        <p className="dosage-converter-disclaimer">
          This tool is for educational purposes only. Always verify calculations independently.
        </p>
      </footer>
    </div>
  );
};

export default DosageConverter;